/**
 * Structural sponsor-wall data for Bursa Bull Charge × The Edge KL Rat Race 2026.
 *
 * Company names, websites and logo files are FACTS, not translatable copy, so
 * they live here — same rule as src/pages/sponsorship/data.ts. Only headings,
 * tier labels and CTA copy go to i18next (namespace "sponsors").
 *
 * ADDING A SPONSOR = one object literal in the right tier's `sponsors` array.
 * Drop the logo in public/sponsor-logos/<tier>/, kebab-case, lowercase
 * extension, no spaces or parentheses (public/ is copied verbatim by Vite, so
 * the filename IS the URL).
 *
 * FIRST, CROP THE FILE TO ITS INK BOUNDS. This is load-bearing, not tidiness:
 * SponsorWall sizes every logo from its aspect ratio and its TIER alone (see
 * LOGO_FILL and TIER_LOGO_SCALE), so baked-in whitespace is indistinguishable
 * from the mark and the logo renders proportionally smaller — which now also
 * reads as the wrong billing tier. Two files have already shipped broken this
 * way — The Edge Malaysia and SD Guthrie both arrived on a 1920x1080 canvas
 * with the mark filling <30% of the height, and a re-exported Mah Sing was 70%
 * whitespace. Keep the uncropped file in ../../../sponsor-source-originals/<tier>/.
 *
 * THEN record the CROPPED file's real pixel dimensions in width/height. A stale
 * pair is silently wrong: it feeds the inline aspect-ratio, so the box shape
 * stops matching the image and object-contain letterboxes the mark.
 */

/**
 * Wall order, top → bottom. `organisers` are the bodies that RUN the event
 * (Bursa × The Edge), not sponsors, so they lead. `officialBank` is a
 * category-exclusive naming right that also sits OUTSIDE the paid ladder; the
 * ladder then reads descending prestige.
 *
 * This order is also the LOGO SIZE order — `organisers` and `officialBank`
 * share the top rung and each tier below renders smaller. The multipliers live
 * in TIER_LOGO_SCALE in SponsorWall.tsx; add a tier here and it needs a rung
 * there (TypeScript enforces it).
 *
 * NOTE this is display order, deliberately the REVERSE of
 * sponsorship/data.ts's `monetaryPackages` (ascending price, flipped at render).
 * Don't "fix" it to match.
 */
export const SPONSOR_TIER_IDS = [
  "organisers",
  "officialBank",
  "platinum",
  "gold",
  "silver",
  "bronze",
  "supporting"
] as const;
export type SponsorTierId = (typeof SPONSOR_TIER_IDS)[number];

export interface Sponsor {
  /** Stable kebab-case id — React key only. */
  id: string;
  /** Brand name. Proper noun → never translated. Doubles as the img alt text. */
  name: string;
  /** Sponsor website, absolute https. Opens in a new tab. */
  href: string;
  /** Site-absolute path under public/sponsor-logos/. */
  logo: string;
  /**
   * Intrinsic pixel size of the CROPPED file. Reserves the box (CLS = 0), feeds
   * the inline aspect-ratio that works around the Safari flex-`img` bug
   * documented in components/layout/Header.tsx, and — because the file is
   * cropped to ink — is also what SponsorWall's equal-area sizing measures.
   */
  width: number;
  height: number;
  /**
   * Escape hatch for ONE logo, defaults to 1. Equal AREA is the right default
   * within a tier, but a very dense mark (solid block) can still read heavier
   * than an airy one at the same area. Nudge that one logo (e.g. 0.92) rather
   * than re-tuning the global scale.
   *
   * This MULTIPLIES the tier's own scale (see TIER_LOGO_SCALE in
   * SponsorWall.tsx), so it stays relative to the logo's own tier — it is not
   * the way to change a whole tier's billing size.
   *
   * Reach for this only after cropping — a logo that looks small is almost
   * always an uncropped canvas, not a scale problem.
   */
  opticalScale?: number;
}

export interface SponsorTier {
  id: SponsorTierId;
  sponsors: Sponsor[];
}

/**
 * Within a tier, list alphabetically — no sponsor gets implied precedence.
 *
 * EXCEPT `organisers`, which is a client-specified sequence (see the tier's own
 * note). Don't re-sort it.
 */
export const sponsorTiers: SponsorTier[] = [
  {
    // The organising bodies, not sponsors. Excluded from `allSponsors` (and so
    // from the sponsors ItemList JSON-LD); they're declared as schema.org
    // `organizer` on the event instead — see seo/structuredData.ts, whose
    // `organizer` array mirrors this ORDER and must be kept in step.
    //
    // Order is the client's, not alphabetical: each corporate family runs
    // parent then foundation (Bursa → Yayasan Bursa, The Edge → TEEF).
    id: "organisers",
    sponsors: [
      {
        id: "bursa-malaysia",
        name: "Bursa Malaysia",
        href: "https://www.bursamalaysia.com",
        logo: "/sponsor-logos/organiser/bursa-malaysia.png",
        width: 1149,
        height: 339
      },
      {
        id: "yayasan-bursa-malaysia",
        name: "Yayasan Bursa Malaysia",
        // No standalone domain — YBM lives as a section of the Bursa site.
        href: "https://www.bursamalaysia.com/about_bursa/sustainability/yayasan-bursa-malaysia/overview",
        logo: "/sponsor-logos/organiser/yayasan-bursa-malaysia.png",
        width: 766,
        height: 279
      },
      {
        id: "the-edge-malaysia",
        name: "The Edge Malaysia",
        href: "https://theedgemalaysia.com",
        // Source art sat in a 16:9 canvas with the mark filling only 27% of the
        // height; cropped to its ink bounds so it reads at the same weight as
        // the rest of the wall.
        logo: "/sponsor-logos/organiser/the-edge-malaysia.png",
        width: 900,
        height: 181
      },
      {
        id: "the-edge-education-foundation",
        name: "The Edge Education Foundation",
        href: "https://teef.org.my/",
        logo: "/sponsor-logos/organiser/the-edge-education-foundation.png",
        // Low-res source (276x72) — slightly soft on retina. Drop a larger file
        // at the same path and update these dimensions; nothing else changes.
        width: 276,
        height: 72
      }
    ]
  },
  {
    id: "officialBank",
    sponsors: [
      {
        id: "uob-malaysia",
        name: "UOB Malaysia",
        href: "https://www.uob.com.my/",
        // Carries a hard white matte rather than transparency — see the bg-card
        // note in SponsorWall.tsx for why the tile must stay pure white.
        logo: "/sponsor-logos/official/uob-malaysia.png",
        width: 900,
        height: 256
      }
    ]
  },
  { id: "platinum", sponsors: [] },
  { id: "gold", sponsors: [] },
  {
    id: "silver",
    sponsors: [
      {
        id: "sd-guthrie",
        // The wordmark as it appears in the logo. The registered entity is
        // "SD Guthrie Berhad", but the wall uses trading names throughout
        // (Vistra, UOB Malaysia) — don't expand it.
        name: "SD Guthrie",
        href: "https://www.sdguthrie.com/",
        // Supplied on a 1920x1080 canvas with the mark filling 29% of the
        // height; cropped to its ink bounds like The Edge Malaysia was.
        logo: "/sponsor-logos/silver/sd-guthrie.png",
        width: 1034,
        height: 314
      }
    ]
  },
  {
    id: "bronze",
    sponsors: [
      {
        id: "dcd-technology",
        name: "DCD Technology",
        href: "https://www.dcdtech.com.my/",
        logo: "/sponsor-logos/bronze/dcd-technology-banner.png",
        /*
         * The WIDE BANNER lockup (monogram + "TECHNOLOGY" + tagline), 6.94:1 —
         * supplied Aug 2026 as `DCD info V1.pdf` and shipped at the client's
         * explicit instruction to size it "similar to the LFG".
         *
         * THE TRADE-OFF IS KNOWN AND WAS ACCEPTED. Do not re-litigate it, and do
         * not "fix" it with an opticalScale nudge. Equal-area sizing turns width
         * into SHORTNESS (height ∝ 1/√ratio), so at 6.94:1 in bronze this renders
         * ~25px tall on the wall and ~18px in the footer, against ~45px/~33px for
         * the 2.14:1 stacked badge it replaces. The tagline lands ~6px and is not
         * legible at either size. The client was shown these numbers and chose to
         * ship anyway.
         *
         * The LFG comparison is what justifies it, and it holds: LFG is 8.98:1 in
         * supporting (0.45) and renders 17.6px / 13.1px. Bronze sits a rung above,
         * so this clears LFG by ~41% on both surfaces. A wide-banner precedent was
         * already live and accepted on the page.
         *
         * A near-identical banner at 6.19:1 WAS shipped and reverted once before,
         * for exactly the smallness above — so if the client complains about size
         * again, the answer is a stacked/compact lockup from DCD (~2.5:1 would
         * render ~42px), NOT a scale nudge here, which would break tier equality.
         * Both older files are kept in ../../../sponsor-source-originals/bronze/.
         *
         * Unlike its predecessors this file has GENUINE ALPHA, not a white matte.
         * Palette-quantized (P + tRNS, 31KB vs 135KB RGBA); verified invisible at
         * render size — max 3-6/255 on <0.5% of pixels, core alpha 254/255.
         *
         * No opticalScale: √6.937 × 0.56 = 1.47, well under the binding caps
         * (Edge Malaysia 2.23 wall / UOB 1.88 strip), so nothing else shrinks.
         */
        width: 1873,
        height: 270
      },
      {
        id: "mah-sing-group",
        name: "Mah Sing Group",
        href: "https://www.mahsing.com.my/",
        // Re-exported at 1774x887 (70% whitespace) after the first release,
        // which left these dimensions stale and shrank the mark on the wall.
        // Now cropped to ink; both source exports are kept in
        // sponsor-source-originals/bronze/.
        logo: "/sponsor-logos/bronze/mah-sing-group.png",
        width: 1133,
        height: 270
      },
      {
        id: "vistra",
        name: "Vistra",
        href: "https://www.vistra.com/",
        // Widest FILE on the wall at 5.34:1, but no longer what caps the sizing:
        // bronze renders at 0.71, so its effective span drops below the top-rung
        // organiser marks. See maxRootSpan in SponsorWall.tsx — the cap is
        // derived, so a wider logo anywhere just recomputes it.
        logo: "/sponsor-logos/bronze/vistra.png",
        width: 769,
        height: 144
      }
    ]
  },
  {
    id: "supporting",
    sponsors: [
      {
        id: "dialog",
        name: "Dialog Group",
        href: "https://www.dialogasia.com/",
        /*
         * Converted from a CMYK print PDF (kept in
         * ../../../sponsor-source-originals/support/), rendered at 200dpi and
         * cropped to ink. CMYK -> RGB shifts colour slightly; the teal lands at
         * #009090. The white inside the wordmark and between the rules is a
         * MATTE, not transparency — invisible only because the sponsor sections
         * are pure white. See WALL_SURFACE in SponsorWall.tsx.
         */
        logo: "/sponsor-logos/support/dialog.png",
        width: 1711,
        height: 787
      },
      {
        id: "lianson-fleet-group",
        name: "Lianson Fleet Group",
        href: "https://lianson.com/",
        /*
         * The positive lockup, which unblocked this: the earlier supply was the
         * WHITE/reversed artwork (still in ../../../sponsor-source-originals/
         * support/LFG_Full_Logo_White_FA.ai), invisible on a white surface.
         * Cropped from a supplied 1920x400 JPEG in which the mark filled only
         * 48% of the height, and re-saved as PNG to match the rest of the set.
         * Like DIALOG this is a white MATTE, not transparency — invisible only
         * because the sponsor sections are pure white (see WALL_SURFACE).
         *
         * WIDEST MARK ON THE SITE at 8.98:1, and that is why it renders SHORT:
         * equal area splits between width and height, so ~17.6px tall on the
         * wall and ~9.5px in the mobile footer — about half its tier-mate
         * DIALOG despite the same rung. The client accepted that over cropping
         * to the LFG monogram (3.64:1, which would render ~27.7px). Do not try
         * to fix it with scale: the ratio is the cause, and only a stacked
         * lockup from LFG changes it.
         *
         * It does NOT constrain anything: its effective span is
         * 0.45 * sqrt(8.98) = 1.35, well under the wall's binding 2.23 and the
         * strip's 1.875 — so ROOT_SPAN_MAX is untouched and no other logo
         * resizes. The wide-logo DEV warning staying silent is correct: that
         * threshold is on the SCALED span, by design.
         */
        logo: "/sponsor-logos/support/lianson-fleet-group.png",
        width: 1724,
        height: 192
      }
    ]
  }
];

/**
 * Tiers that actually have logos — see SHOW_EMPTY_TIER_SLOTS in SponsorWall.tsx.
 * Includes `organisers`. This is what the /sponsors WALL renders; the footer
 * strip renders `stripSponsorTiers` below.
 */
export const filledSponsorTiers = sponsorTiers.filter((tier) => tier.sponsors.length > 0);

/**
 * What the site-wide footer strip renders: sponsors only.
 *
 * Organisers are excluded at CLIENT REQUEST — they run the event rather than
 * buy into it, and they're already credited in full on /sponsors, which is the
 * page that exists for it. Don't "fix" this back to `filledSponsorTiers`.
 */
export const stripSponsorTiers = filledSponsorTiers.filter((tier) => tier.id !== "organisers");

/**
 * Sponsors only — feeds the ItemList JSON-LD so search/AI crawlers can read the
 * names. Organisers are deliberately EXCLUDED: they run the event, so putting
 * them in a list titled "Sponsors" would misstate the relationship.
 */
export const allSponsors = sponsorTiers
  .filter((tier) => tier.id !== "organisers")
  .flatMap((tier) => tier.sponsors);

/** The organising bodies — mirrored into schema.org `organizer` on the event. */
export const allOrganisers =
  sponsorTiers.find((tier) => tier.id === "organisers")?.sponsors ?? [];
